<template>
    <div>
        <FullPageLoader :isLoading="loading"/>

        <div class="row">
            <div class="column medium-4 text-right float-right">
                <b-tooltip :label="hideSensitiveData ? 'Toon gevoelige data' : 'Verberg gevoelige data'" position="is-left">
                    <b-button :type="hideSensitiveData ? 'is-success' : 'is-danger'" :icon-right="hideSensitiveData ? 'eye' : 'eye-slash'" @click="$store.dispatch('toggleSensitiveData')"></b-button>
                </b-tooltip>
                <b-button tag="router-link" to="/panel/klanten/" type="is-dark">Terug naar overzicht</b-button>
            </div>
            <div class="column medium-8">
                <h1>{{ $route.params.id == 'nieuw' ? 'Klant toevoegen' : name }}</h1>
            </div>
        </div>

        <div class="row" v-if="!notDraft">
            <div class="column">
                <b-notification type="is-warning">
                    <span>Deze klant is nog niet opgeslagen. Controleer de gegevens en klik op 'nieuwe klant opslaan'.</span>
                </b-notification>
            </div>
        </div>

        <div class="row">
            <div class="column">
                <form class="form">
                    <b-tabs size="is-medium" class="block" v-model="tabIndex">
                        <b-tab-item label="Gegevens">
                            <CRUDCustomer
                                :id="$route.params.id == 'nieuw' ? 'nieuw' : parseInt($route.params.id)"
                                type="edit"
                                embedded_from="CustomerRecord"
                                :canRemoveCustomer="false"
                                @passCustomer="passCustomer"
                                @passName="passName"
                                @fullPageLoader="fullPageLoader"
                                @passDraft="passDraft"
                            />
                        </b-tab-item>

                        <b-tab-item label="Contracten" v-if="$route.params.id != 'nieuw' && notDraft">
                            <div class="text-right">
                                <b-button
                                    tag="router-link"
                                    v-if="!user.deurwaarder"
                                    :to="{ name: 'ContractsRecord', params: {id: 'nieuw'}, query: {customer: $route.params.id} }"
                                    type="is-primary"
                                >
                                    Contract aanmaken
                                </b-button>
                            </div>
                            <ContractsOverviewTable
                                ref="ContractsOverviewTable"
                                :getFilters="{customer: $route.params.id}"
                            />
                        </b-tab-item>

                        <b-tab-item label="Facturen" v-if="$route.params.id != 'nieuw' && notDraft">
                            <div class="text-right">
                                <b-button tag="router-link" v-if="!user.deurwaarder" :to="{ name: 'CreateInvoiceRecord', query: {customer: $route.params.id, type: 'los'} }" type="is-primary" icon-left="euro-sign">Factuur maken</b-button>
                            </div>
                            <InvoicesTable
                                ref="InvoicesTable"
                                type="customer"
                                :id="$route.params.id"
                            />
                        </b-tab-item>

                        <b-tab-item label="Borg" headerClass="crediteren" v-if="$route.params.id != 'nieuw' && notDraft">
                            <CustomerCredits
                                :customerId="$route.params.id"
                            />
                        </b-tab-item>

                        <b-tab-item label="Toegang" v-if="(user.role == 'pit' || user.organisation_connect) && !user.deurwaarder && notDraft">
                            <template slot="header">
                                Toegang&nbsp;
                                <b-icon v-if="connectLoaded" icon="key" size="is-small" :class="{
                                    'has-text-danger': hasValidAccess === false,
                                    'has-text-success': hasValidAccess === true,
                                    'has-text-grey': hasValidAccess === null
                                }" />
                                <b-icon v-else icon="spinner" size="is-small" class="fa-spin" />
                            </template>

                            <div class="row">
                                <div class="column small-7">
                                    <h1 class="title">Toegang</h1>
                                </div>
                                <div class="column small-5 text-right">
                                    <b-button type="is-primary" tag="router-link" :to="{ name: 'ConnectRecord', params: {id: 'nieuw'}, query: {customer: $route.params.id} }">Toegang aanmaken</b-button>
                                </div>
                            </div>

                            <ConnectTable
                                type="user"
                                :id="$route.params.id"
                                @connectloaded="connectLoaded = true"
                                @open="setHasValidAccess"
                            />
                        </b-tab-item>

                        <b-tab-item label="Logs">
                            <!-- Whole lot of nothing. -->
                            <!-- Im just here to show the communication logs. -->
                        </b-tab-item>

                        <!-- TODO; Werkt voor geen meter. -->
                        <!-- <b-tab-item label="Logs" v-if="user && user.role == 'pit'">
                            <h3>Logs</h3>

                            <LogsRecord
                                ref="LogsRecord"
                                :getFilters="{0: {name: $route.params.id, query: { subject_type: 'App\\Customer', subject_id: $route.params.id }}}"
                            />
                        </b-tab-item> -->
                    </b-tabs>
                </form>
            </div>
        </div>

        <!-- Communicatielog -->
        <CommunicationLogs
            v-if="$route.params.id != 'nieuw'"
            type="customer"
            :logs="logs"
            :loading="loading"
            :viewRelatedButton="relatedLogs"
            :customer="customer"
            @loadCustomerLogs="loadCustomerLogs"
            @toggleRelated="toggleRelated"
        />
    </div>
</template>

<script>
import InvoicesTable from '@/vue/invoices/components/InvoicesTable'
import CustomerCredits from '@/vue/customers/CustomerCredits'
import ConnectTable from '@/vue/connect/components/ConnectTable'
import CommunicationLogs from '@/vue/CommunicationLogs'
import ContractsOverviewTable from '@/vue/contracts/ContractsOverviewTable'

export default {
    name: 'CustomersRecord',

    components: {
        CRUDCustomer: () => import('@/vue/CRUDCustomer'),
        InvoicesTable,
        CustomerCredits,
        ConnectTable,
        'LogsRecord': () => import('@/vue/logs/LogsRecord'),
        CommunicationLogs,
        ContractsOverviewTable
    },

    data () {
        return {
            customer: null,
            name: null,
            mode: 'view',
            loading: true,
            tabIndex: 0,
            notDraft: true,
            logs: [],
            relatedLogs: false,
            // saving: false,
            // errors: {}

            // Connect/Access
            connectLoaded: false,
            hasValidAccess: null,
        }
    },

    computed: {
        user() {
            return this.$store.state.user
        },

        hideSensitiveData() {
            return this.$store.state.hideSensitiveData
        }
    },

    watch: {
        tabIndex(newIndex) {
            // Tab index 2 is the "Facturen" (Invoices) tab
            if (newIndex === 2 && this.$refs['InvoicesTable']) {
                this.$nextTick(() => {
                    this.$refs['InvoicesTable']?.loadAsyncData()
                })
            }
        }
    },

    created() {
        this.loadCustomerLogs()
    },

    mounted() {
        this.$refs['ContractsOverviewTable']?.loadAsyncData()
        this.$refs['InvoicesTable']?.loadAsyncData()
    },

    methods: {
        loadCustomerLogs() {
            this.loading = true

            axios.get(`/api/customer/${this.$route.params.id}/logs?related=${this.relatedLogs}`, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                this.logs = this.relatedLogs ? response.data : [...response.data]
            }).then(() => {
                this.loading = false
            })
        },

        fullPageLoader(value){
            this.loading = value
        },

        passName(value){
            this.name = value
        },

        passDraft(value){
            this.notDraft = !value
        },

        toggleRelated () {
            this.relatedLogs = !this.relatedLogs
            this.loadCustomerLogs()
        },

        scrollToCommunicationLogs() {
            const element = document.querySelector('.communication-logs')
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' })
            }
        },

        passCustomer(customer) {
            this.customer = customer
        },

        // Set the hasValidAccess variable, to show if the box is connected with an connect access
        setHasValidAccess(hasValidAccess){
            this.hasValidAccess = hasValidAccess
        }
    }
}
</script>

<style scoped>
.tab-button {
    margin-top: 0.5em;
    font-size: 1.25em;
}
</style>
